import { useDispatch } from 'react-redux';
import {
  useGetModalMetaData,
  useIsPlainModalOpenSelector,
} from '../../../../../../store/modules/modals/modals.selector';
import {
  closeModalByNameAction,
  PlainModals,
} from '../../../../../../store/modules/modals/modals.reducer';
import { ModalContainer } from '../../../../../../components/modals/modal-container/modal-container';
import React, { useContext } from 'react';
import { IdentityRiskReasons } from '../../../../../../store/apis/types';
import { TableComponent } from '../../../../../../components/table/table-component/table-component';
import { tableDetectionsStructure } from './constant';
import { useNavigate } from 'react-router-dom';
import { ApiContext } from '../../../../../../components/providers/api-provider/api-provider';
import { TableCellWithLink } from '../../../../../../components/table/table-components/table-components';
import { SeverityChip } from '../../../../../policies/components/severity-chip/severity-chip';
import {
  IDENTITY_DETAILS_RISK_REASON_TABLE,
  IDENTITY_RISK_REASON_POLICY_LINK,
} from 'constants/test-ids';
import { Box, Typography } from '@mui/material';
import { fontSecondary } from 'constants/font-family';
import { FontSizes } from 'constants/font-sizes';
import { Colors } from 'constants/colors';
import { TableFiltersContainer } from 'components/table/styles';

interface RiskReasonTableData {
  name: string;
  severity: number;
  count: number;
}

export const RiskReasonModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const metaData = useGetModalMetaData();
  const apiContext = useContext(ApiContext);
  const { data, isLoading, isError } =
    metaData.type === 'identity'
      ? apiContext.baseApi?.useGetIdentityRiskReasonsQuery(
          metaData.id ? metaData.id : '',
          {
            refetchOnMountOrArgChange: true,
          },
        )
      : apiContext.baseApi?.useGetRepositoryRiskReasonsQuery(
          metaData.id ? metaData.id : '',
          {
            refetchOnMountOrArgChange: true,
          },
        );

  const generateRiskReasonTableData = (riskReasonRes: IdentityRiskReasons) => {
    const riskReasonTableData: RiskReasonTableData[] = [];
    for (const severity in riskReasonRes.data.findings) {
      for (const riskDetail of riskReasonRes.data.findings[severity]) {
        riskReasonTableData.push({
          severity: parseInt(severity),
          ...riskDetail,
        });
      }
    }
    riskReasonTableData.sort((a, b) => b.severity - a.severity);
    return riskReasonTableData;
  };

  const isRiskReasonEvidenceOpenModal = useIsPlainModalOpenSelector(
    PlainModals.IDENTITIES_DETAILS_RISK_REASON_MODAL,
  );

  const handleCloseModal = () => {
    dispatch(
      closeModalByNameAction(PlainModals.IDENTITIES_DETAILS_RISK_REASON_MODAL),
    );
  };

  const navigatePolicy = (policyName: string) => {
    localStorage.setItem('policyLogin', metaData?.id || '');
    handleCloseModal();
    navigate(`/policy?policy=${policyName}`);
  };

  const renderPolicyName = (data: RiskReasonTableData) => (
    <TableCellWithLink
      handleClick={() => navigatePolicy(data.name)}
      isActive={true}
      itemTitle={data.name}
      dataTestId={IDENTITY_RISK_REASON_POLICY_LINK}
    />
  );

  const renderIgnoredPolicyName = (data: any) => (
    <TableCellWithLink
      handleClick={() => navigatePolicy(data.policy)}
      isActive={true}
      itemTitle={data.policy}
      dataTestId={IDENTITY_RISK_REASON_POLICY_LINK}
    />
  );

  const renderSeverityChip = (data: RiskReasonTableData) => (
    <SeverityChip severityScore={data.severity} />
  );

  const navigateDetectionResults = (policyName: string) => {
    localStorage.setItem('policyLogin', metaData?.id || '');
    localStorage.setItem('userFullName', metaData.userFullName || '');
    handleCloseModal();
    navigate(`/policy-criteria?policy=${policyName}`);
  };

  const renderNavigatePolicy = (data: RiskReasonTableData) => (
    <TableCellWithLink
      handleClick={() => navigateDetectionResults(data?.name)}
      isActive={true}
      itemTitle={''}
      dataTestId={IDENTITY_RISK_REASON_POLICY_LINK}
      itemType={'internal'}
    />
  );

  const renderIgnoredNavigatePolicy = (data: any) => (
    <TableCellWithLink
      handleClick={() => navigateDetectionResults(data?.policy)}
      isActive={true}
      itemTitle={''}
      dataTestId={IDENTITY_RISK_REASON_POLICY_LINK}
      itemType={'internal'}
    />
  );

  const extraCellRendererObj = {
    name: renderPolicyName,
    policy: renderIgnoredPolicyName,
    severity: renderSeverityChip,
    action: renderNavigatePolicy,
    ignored_action: renderIgnoredNavigatePolicy,
  };

  return (
    <ModalContainer
      isOpenModal={isRiskReasonEvidenceOpenModal}
      title="Risk Reasons"
      handleCloseModal={handleCloseModal}
      modalWidth="1050px"
      modalHeight="600px"
    >
      <TableFiltersContainer>
        <Box sx={{ mt: 2 }}>
          <Typography
            sx={{
              fontFamily: fontSecondary,
              fontSize: FontSizes.fontSize16,
              fontWeight: 600,
              lineHeight: '20px',
              color: Colors.blackPearl,
            }}
          >
            Active Detections
          </Typography>
        </Box>
      </TableFiltersContainer>
      <TableComponent
        tableId={IDENTITY_DETAILS_RISK_REASON_TABLE}
        isListLoading={isLoading}
        isListErrored={isError}
        isEmptyList={
          generateRiskReasonTableData(data || { data: { findings: [] } })
            ?.length
            ? false
            : true
        }
        tableMinHeight="100px"
        isListFetching={false}
        dataTestId={IDENTITY_DETAILS_RISK_REASON_TABLE}
        list={generateRiskReasonTableData(data || { data: { findings: [] } })}
        tableColumns={
          metaData.type !== 'identity'
            ? tableDetectionsStructure.slice(0, -1)
            : tableDetectionsStructure
        }
        extraCellsRenderObj={extraCellRendererObj}
        notPaginated
      />
    </ModalContainer>
  );
};
